import React, { useCallback, useEffect } from "react";
import { Image, ModalProps } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useAtomValue } from "jotai";

import "./ForfeitGameModal.scss";
import GhostButton from "../../dashboard-page/GhostButton/GhostButton";
import CloseRoundedIcon from "../../icons/CloseRoundedIcon";
import { CustomSpinner } from "../../CustomSpinner/CustomSpinner";
import useForfeitTournamentEntryMutation from "../../../react-query/mutations/use-forfeit-tournament-entry";
import BottomDrawer from "../../BottomDrawer/BottomDrawer";
import useAnalytics from "../../../hooks/useAnalytics";
import { GameType } from "../../../utils/constants";
import useAuth from "../../../hooks/useAuth";
import PlayIconFlilled from "../../icons/PlayIconFlilled";
import PrimaryActionButton from "../../Buttons/PrimaryActionButton/PrimaryActionButton";
import DefaultAvatar from "../../../images/fallback-avatar.png";
import { userCustomAvatarAtom } from "../../../jotai/atoms/user";

interface ForfeitGameModalProps extends Pick<ModalProps, "show"> {
  redirectUrl?: string;
  entryId: string;
  thumbnail?: string;
  stakeAmount: number;
  gameName: string;
  gameType: GameType;
  refetch?: () => void;
  onHide: () => void;
  onReplay?: () => void;
}

function ForfeitGameModal({
  show,
  onHide,
  redirectUrl,
  entryId,
  thumbnail,
  stakeAmount,
  gameName,
  gameType,
  refetch,
  onReplay,
}: ForfeitGameModalProps) {
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const { userProfile } = useAuth();
  const userCustomAvatar = useAtomValue(userCustomAvatarAtom);
  const {
    forfeitEntry,
    mutation: { isPending, isSuccess },
  } = useForfeitTournamentEntryMutation(redirectUrl);

  useEffect(() => {
    if (!isSuccess) return;
    analytics.sendCancelGameEvent({
      entry_fee: stakeAmount,
      game_name: gameName,
      game_mode: gameType,
    });
    refetch && refetch();
  }, [isSuccess, analytics, gameName, gameType, stakeAmount, refetch]);

  const handleReplayClick = useCallback(() => {
    if (onReplay) {
      onReplay();
    }
    onHide();
  }, [onReplay, onHide]);

  return (
    <BottomDrawer isOpen={!!show} onClose={onHide} showCloseBtn={true}>
      <div className="forfeit-game-modal">
        <div>
          <div className="img-box">
            <div className="avatars-wrapper">
              <Image
                src={
                  userCustomAvatar || userProfile?.avatar?.url || DefaultAvatar
                }
                alt={userProfile?.avatar?.label}
                height={72}
                width={72}
                className="user-avatar"
              />
              <CloseRoundedIcon className="cancel-icon" />
              <Image
                src={thumbnail}
                alt={`${gameName} thumbnail`}
                height={72}
                width={72}
                className="game-thumbnail"
              />
            </div>
          </div>
          <h2 className="heading">{t("forfeit_game")}</h2>
          <p className="content">
            {t("are_you_sure_you_want_to_forfeit_this_game_key")}
          </p>
          <PrimaryActionButton
            text={
              <span className="d-inline-flex align-items-center justify-content-center gap-2">
                <PlayIconFlilled fill="white" />
                <span>{t("replay")}</span>
              </span>
            }
            buttonClassName="w-100 mb-3 text-capitalize"
            onClick={handleReplayClick}
          />
          <GhostButton
            className="cancel-btn"
            onClick={() => forfeitEntry(entryId)}
            disabled={isPending}
          >
            <span className="d-flex align-items-center gap-1">
              {t("forfeit_game")}
            </span>
            {isPending && <CustomSpinner size="sm" className="ms-1" />}
          </GhostButton>
        </div>
      </div>
    </BottomDrawer>
  );
}

export default ForfeitGameModal;
